import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { useWorkspaceMetadataStore } from '@/stores/workspaceMetadataStore';
import { workspaceApi } from '@/lib/workspaceApi';
import { workspaceMemberApi, WorkspaceMember } from '@/lib/workspaceMemberApi';
import { Column } from '@/lib/columnApi';
import { Category, Tag } from '@/lib/itemApi';
import { Button, Input, Label, Badge, Tabs, TabsList, TabsTrigger, TabsContent, Select, SelectItem, Checkbox } from '@fyos/components';
import { X, Plus, Trash2, UserPlus, Shield, Crown, Eye, EyeOff, Pencil, Users, Settings, Columns3, Palette, Tag as TagIcon, FolderOpen, Check, ChevronUp, ChevronDown } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import RichTextEditor from './RichTextEditor';
import ColorPicker from './ColorPicker';

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
}

export default function SettingsModal({ open, onClose, workspaceId }: SettingsModalProps) {
  const { user } = useAuthStore();
  const { currentWorkspace, setCurrentWorkspace } = useWorkspaceStore();

  // Subscribe to metadata store for columns, categories, tags
  const columns = useWorkspaceMetadataStore((state) => state.columns);
  const categories = useWorkspaceMetadataStore((state) => state.categories);
  const tags = useWorkspaceMetadataStore((state) => state.tags);
  const columnsLoading = useWorkspaceMetadataStore((state) => state.columnsLoading);
  const categoriesLoading = useWorkspaceMetadataStore((state) => state.categoriesLoading);
  const tagsLoading = useWorkspaceMetadataStore((state) => state.tagsLoading);
  const { updateColumn, reorderColumns, createCategory, updateCategory, deleteCategory, createTag, updateTag, deleteTag, createColumn, deleteColumn } = useWorkspaceMetadataStore();

  // General settings
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Members
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [removeMember, setRemoveMember] = useState<WorkspaceMember | null>(null);

  // Columns - form state only
  const [newColumnName, setNewColumnName] = useState('');
  const [newColumnColor, setNewColumnColor] = useState('#6B7280');
  const [newColumnDescription, setNewColumnDescription] = useState('');
  const [creatingColumn, setCreatingColumn] = useState(false);
  const [deleteColumnItem, setDeleteColumnItem] = useState<Column | null>(null);
  const [editingColumn, setEditingColumn] = useState<string | null>(null);
  const [editColumnName, setEditColumnName] = useState('');
  const [editColumnColor, setEditColumnColor] = useState('');
  const [editColumnDescription, setEditColumnDescription] = useState('');

  // Categories - form state only
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#6B7280');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [deleteCategoryItem, setDeleteCategoryItem] = useState<Category | null>(null);
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryColor, setEditCategoryColor] = useState('');

  // Tags - form state only
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');
  const [creatingTag, setCreatingTag] = useState(false);
  const [deleteTagItem, setDeleteTagItem] = useState<Tag | null>(null);
  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [editTagName, setEditTagName] = useState('');
  const [editTagColor, setEditTagColor] = useState('');

  // Branding / Theming
  const [primaryColor, setPrimaryColor] = useState('#7C3AED');
  const [secondaryColor, setSecondaryColor] = useState('#10B981');
  const [fontFamily, setFontFamily] = useState('Inter');
  const [customCss, setCustomCss] = useState('');
  const [savingTheme, setSavingTheme] = useState(false);
  const [themeSuccess, setThemeSuccess] = useState(false);

  useEffect(() => {
    if (currentWorkspace) {
      setName(currentWorkspace.name || '');
      setDescription(currentWorkspace.description || '');
      setIsPublic(!!currentWorkspace.is_public);
      const t = currentWorkspace.theme as Record<string, string> | undefined;
      if (t) {
        setPrimaryColor(t.primary_color || '#7C3AED');
        setSecondaryColor(t.secondary_color || '#10B981');
        setFontFamily(t.font_family || 'Inter');
        setCustomCss(t.custom_css || '');
      }
    }
  }, [currentWorkspace]);

  useEffect(() => {
    if (open && workspaceId) {
      fetchMembers();
      useWorkspaceMetadataStore.getState().fetchAll(workspaceId);
    }
  }, [open, workspaceId]);

  const fetchMembers = async () => {
    try {
      setMembersLoading(true);
      const data = await workspaceMemberApi.getAll(workspaceId);
      setMembers(data);
    } catch (err) {
      console.error('Failed to fetch members:', err);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleToggleColumnVisibility = async (col: Column) => {
    try {
      await updateColumn(workspaceId, col.id, { is_active: !col.is_active });
    } catch (err) {
      console.error('Failed to toggle column visibility:', err);
    }
  };

  const handleMoveColumn = async (index: number, direction: 'up' | 'down') => {
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= columns.length) return;
    const reordered = [...columns];
    [reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]];
    try {
      await reorderColumns(workspaceId, reordered.map(c => c.id));
    } catch (err) {
      console.error('Failed to reorder columns:', err);
    }
  };

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    try {
      setCreatingCategory(true);
      await createCategory(workspaceId, {
        name: newCategoryName,
        color: newCategoryColor,
        sort_order: categories.length,
      });
      setNewCategoryName('');
      setNewCategoryColor('#6B7280');
    } catch (err) {
      console.error('Failed to create category:', err);
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleUpdateCategory = async (id: string) => {
    if (!editCategoryName.trim()) return;
    try {
      await updateCategory(workspaceId, id, {
        name: editCategoryName,
        color: editCategoryColor,
      });
      setEditingCategory(null);
    } catch (err) {
      console.error('Failed to update category:', err);
    }
  };

  const confirmDeleteCategory = async () => {
    if (!deleteCategoryItem) return;
    try {
      await deleteCategory(workspaceId, deleteCategoryItem.id);
      setDeleteCategoryItem(null);
    } catch (err) {
      console.error('Failed to delete category:', err);
    }
  };

  const handleCreateTag = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTagName.trim()) return;
    try {
      setCreatingTag(true);
      await createTag(workspaceId, {
        name: newTagName,
        color: newTagColor,
        sort_order: tags.length,
      });
      setNewTagName('');
      setNewTagColor('#3B82F6');
    } catch (err) {
      console.error('Failed to create tag:', err);
    } finally {
      setCreatingTag(false);
    }
  };

  const handleUpdateTag = async (id: string) => {
    if (!editTagName.trim()) return;
    try {
      await updateTag(workspaceId, id, {
        name: editTagName,
        color: editTagColor,
      });
      setEditingTag(null);
    } catch (err) {
      console.error('Failed to update tag:', err);
    }
  };

  const confirmDeleteTag = async () => {
    if (!deleteTagItem) return;
    try {
      await deleteTag(workspaceId, deleteTagItem.id);
      setDeleteTagItem(null);
    } catch (err) {
      console.error('Failed to delete tag:', err);
    }
  };

  const handleSaveGeneral = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      const updated = await workspaceApi.update(workspaceId, { name, description, is_public: isPublic });
      setCurrentWorkspace(updated);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteFirstName.trim() || !inviteEmail.trim()) return;
    try {
      setInviting(true);
      setInviteError('');
      const member = await workspaceMemberApi.invite(workspaceId, inviteEmail, inviteFirstName, inviteRole);
      setMembers([...members, member]);
      setInviteFirstName('');
      setInviteEmail('');
      setInviteRole('member');
    } catch (err: any) {
      setInviteError(err.response?.data?.message || 'Failed to invite member');
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (memberId: string, newRole: string) => {
    try {
      await workspaceMemberApi.updateRole(workspaceId, memberId, newRole);
      setMembers(members.map(m => m.id === memberId ? { ...m, role: newRole as WorkspaceMember['role'] } : m));
    } catch (err: any) {
      console.error('Failed to update role:', err);
    }
  };

  const confirmRemoveMember = async () => {
    if (!removeMember) return;
    try {
      await workspaceMemberApi.remove(workspaceId, removeMember.id);
      setMembers(members.filter(m => m.id !== removeMember.id));
      setRemoveMember(null);
    } catch (err) {
      console.error('Failed to remove member:', err);
    }
  };

  const handleCreateColumn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newColumnName.trim()) return;
    try {
      setCreatingColumn(true);
      await createColumn(workspaceId, {
        name: newColumnName,
        description: newColumnDescription || undefined,
        color: newColumnColor,
        sort_order: columns.length,
      });
      setNewColumnName('');
      setNewColumnDescription('');
      setNewColumnColor('#6B7280');
    } catch (err) {
      console.error('Failed to create column:', err);
    } finally {
      setCreatingColumn(false);
    }
  };

  const handleUpdateColumn = async (id: string) => {
    if (!editColumnName.trim()) return;
    try {
      await updateColumn(workspaceId, id, {
        name: editColumnName,
        description: editColumnDescription || undefined,
        color: editColumnColor,
      });
      setEditingColumn(null);
    } catch (err) {
      console.error('Failed to update column:', err);
    }
  };

  const confirmDeleteColumn = async () => {
    if (!deleteColumnItem) return;
    try {
      await deleteColumn(workspaceId, deleteColumnItem.id);
      setDeleteColumnItem(null);
    } catch (err) {
      console.error('Failed to delete column:', err);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <Crown className="h-4 w-4 text-amber-500" />;
      case 'manager': return <Shield className="h-4 w-4 text-blue-500" />;
      case 'viewer': return <Eye className="h-4 w-4 text-gray-400" />;
      default: return <Pencil className="h-4 w-4 text-green-500" />;
    }
  };

  const handleSaveTheme = async () => {
    try {
      setSavingTheme(true);
      setThemeSuccess(false);
      const theme = { primary_color: primaryColor, secondary_color: secondaryColor, font_family: fontFamily, custom_css: customCss };
      const updated = await workspaceApi.update(workspaceId, { theme });
      setCurrentWorkspace(updated);
      setThemeSuccess(true);
      setTimeout(() => setThemeSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save theme:', err);
    } finally {
      setSavingTheme(false);
    }
  };

  const isOwner = currentWorkspace?.owner_id === user?.id;
  const canManage = currentWorkspace?.role === 'admin' || currentWorkspace?.role === 'manager';

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-50 w-full max-w-2xl bg-white dark:bg-gray-800 shadow-xl flex flex-col animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Settings</h2>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <Tabs defaultValue="general">
            <TabsList className="grid grid-cols-6 w-full h-auto rounded-xl bg-gray-100 dark:bg-gray-800 p-1">
              <TabsTrigger value="general" className="flex-col gap-0.5 py-2 px-0 rounded-lg text-[10px] uppercase tracking-wide">
                <Settings className="h-4 w-4" />
                General
              </TabsTrigger>
              <TabsTrigger value="members" className="flex-col gap-0.5 py-2 px-0 rounded-lg text-[10px] uppercase tracking-wide">
                <Users className="h-4 w-4" />
                Members
              </TabsTrigger>
              <TabsTrigger value="columns" className="flex-col gap-0.5 py-2 px-0 rounded-lg text-[10px] uppercase tracking-wide">
                <Columns3 className="h-4 w-4" />
                Columns
              </TabsTrigger>
              <TabsTrigger value="categories" className="flex-col gap-0.5 py-2 px-0 rounded-lg text-[10px] uppercase tracking-wide">
                <FolderOpen className="h-4 w-4" />
                Categories
              </TabsTrigger>
              <TabsTrigger value="tags" className="flex-col gap-0.5 py-2 px-0 rounded-lg text-[10px] uppercase tracking-wide">
                <TagIcon className="h-4 w-4" />
                Tags
              </TabsTrigger>
              <TabsTrigger value="branding" className="flex-col gap-0.5 py-2 px-0 rounded-lg text-[10px] uppercase tracking-wide">
                <Palette className="h-4 w-4" />
                Branding
              </TabsTrigger>
            </TabsList>

            {/* General Tab */}
            <TabsContent value="general">
              <div className="mt-6 space-y-6">
                <div>
                  <Label htmlFor="ws-name">Workspace Name</Label>
                  <Input
                    id="ws-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="ws-desc">Description</Label>
                  <div className="mt-1">
                    <RichTextEditor
                      content={description}
                      onChange={setDescription}
                      placeholder="Describe your workspace..."
                      minHeight="80px"
                    />
                  </div>
                </div>
                {isOwner && (
                  <Checkbox
                    id="ws-public"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                    label="Make this workspace public (visible on public roadmap)"
                  />
                )}
                <div className="flex items-center gap-3">
                  <Button onClick={handleSaveGeneral} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  {saveSuccess && (
                    <span className="text-sm text-green-600 dark:text-green-400">Saved successfully</span>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Members Tab */}
            <TabsContent value="members">
              <div className="mt-6 space-y-6">
                {canManage && (
                  <form onSubmit={handleInvite} className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label htmlFor="invite-first-name">First Name</Label>
                      <Input
                        id="invite-first-name"
                        type="text"
                        value={inviteFirstName}
                        onChange={(e) => setInviteFirstName(e.target.value)}
                        placeholder="John"
                        className="mt-1"
                      />
                    </div>
                    <div className="flex-1">
                      <Label htmlFor="invite-email">Email</Label>
                      <Input
                        id="invite-email"
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="colleague@company.com"
                        className="mt-1"
                      />
                    </div>
                    <div className="w-32">
                      <Label htmlFor="invite-role">Role</Label>
                      <Select value={inviteRole} onValueChange={setInviteRole}>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </Select>
                    </div>
                    <Button type="submit" disabled={inviting || !inviteFirstName.trim() || !inviteEmail.trim()}>
                      <UserPlus className="h-4 w-4 mr-1.5" />
                      {inviting ? 'Inviting...' : 'Invite'}
                    </Button>
                  </form>
                )}
                {inviteError && (
                  <div className="text-sm text-red-600 dark:text-red-400">{inviteError}</div>
                )}

                <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {membersLoading ? (
                    <div className="p-8 text-center text-gray-500">Loading members...</div>
                  ) : (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Member</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Role</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Joined</th>
                          {canManage && (
                            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Actions</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {members.map((member) => {
                          const isSelf = member.user_id === user?.id;
                          const isMemberOwner = member.user_id === currentWorkspace?.owner_id;
                          return (
                            <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <div className="h-8 w-8 rounded-full bg-brand-100 dark:bg-brand-900 flex items-center justify-center text-sm font-medium text-brand-700 dark:text-brand-300">
                                    {member.name?.charAt(0)?.toUpperCase() || '?'}
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {member.name}
                                      {isSelf && <span className="ml-1.5 text-xs text-gray-400">(you)</span>}
                                      {isMemberOwner && <Crown className="inline h-3.5 w-3.5 ml-1 text-amber-500" />}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{member.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                {canManage && !isSelf && !isMemberOwner ? (
                                  <Select value={member.role} onValueChange={(val) => handleRoleChange(member.id, val)}>
                                    <SelectItem value="admin">Admin</SelectItem>
                                    <SelectItem value="manager">Manager</SelectItem>
                                    <SelectItem value="member">Member</SelectItem>
                                    <SelectItem value="viewer">Viewer</SelectItem>
                                  </Select>
                                ) : (
                                  <div className="flex items-center gap-1.5">
                                    {getRoleIcon(member.role)}
                                    <span className="text-sm capitalize text-gray-700 dark:text-gray-300">{member.role}</span>
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                {member.joined_at ? new Date(member.joined_at).toLocaleDateString() : '—'}
                              </td>
                              {canManage && (
                                <td className="px-4 py-3 text-right">
                                  {!isSelf && !isMemberOwner && (
                                    <Button variant="ghost" size="sm" onClick={() => setRemoveMember(member)}>
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Columns Tab */}
            <TabsContent value="columns">
              <div className="mt-6 space-y-6">
                {canManage && (
                  <form onSubmit={handleCreateColumn} className="space-y-3">
                    <div className="flex items-end gap-3">
                      <div className="flex-1">
                        <Label htmlFor="col-name">New Column</Label>
                        <Input
                          id="col-name"
                          value={newColumnName}
                          onChange={(e) => setNewColumnName(e.target.value)}
                          placeholder="e.g. In Review"
                          className="mt-1"
                        />
                      </div>
                      <div className="w-44">
                        <Label htmlFor="col-color">Color</Label>
                        <div className="mt-1">
                          <ColorPicker id="col-color" value={newColumnColor} onChange={setNewColumnColor} />
                        </div>
                      </div>
                      <Button type="submit" disabled={creatingColumn || !newColumnName.trim()}>
                        <Plus className="h-4 w-4 mr-1.5" />
                        Add
                      </Button>
                    </div>
                    <div>
                      <Label htmlFor="col-desc">Description (optional)</Label>
                      <Input
                        id="col-desc"
                        value={newColumnDescription}
                        onChange={(e) => setNewColumnDescription(e.target.value)}
                        placeholder="Describe what this column represents..."
                        className="mt-1"
                      />
                    </div>
                  </form>
                )}

                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Drag to reorder columns. Toggle visibility to show or hide columns on the roadmap.
                </p>

                <div className="space-y-2">
                  {columnsLoading ? (
                    <div className="p-8 text-center text-gray-500">Loading columns...</div>
                  ) : (
                    columns.map((col, index) => (
                      <div
                        key={col.id}
                        className={`rounded-lg border px-4 py-3 ${
                          col.is_active
                            ? 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
                            : 'border-gray-100 bg-gray-50 opacity-60 dark:border-gray-800 dark:bg-gray-900'
                        }`}
                      >
                        {editingColumn === col.id ? (
                          <div className="space-y-3">
                            <div className="flex items-center gap-3">
                              <ColorPicker size="sm" value={editColumnColor} onChange={setEditColumnColor} />
                              <Input
                                value={editColumnName}
                                onChange={(e) => setEditColumnName(e.target.value)}
                                className="flex-1"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleUpdateColumn(col.id); }
                                  if (e.key === 'Escape') setEditingColumn(null);
                                }}
                              />
                              <Button size="sm" onClick={() => handleUpdateColumn(col.id)}>
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setEditingColumn(null)}>
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            <div>
                              <Label htmlFor={`col-desc-${col.id}`} className="text-xs">Description (optional)</Label>
                              <Input
                                id={`col-desc-${col.id}`}
                                value={editColumnDescription}
                                onChange={(e) => setEditColumnDescription(e.target.value)}
                                placeholder="Describe what this column represents..."
                                className="mt-1 text-sm"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-wrap">
                              <div
                                className="h-4 w-4 rounded-full flex-shrink-0"
                                style={{ backgroundColor: col.color || '#6B7280' }}
                              />
                              <span className={`text-sm font-medium ${col.is_active ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500 line-through'}`}>
                                {col.name}
                              </span>
                              <span className="text-xs text-gray-400">({col.slug})</span>
                              {col.is_default && (
                                <Badge variant="gray" size="sm">default</Badge>
                              )}
                              {!col.is_active && (
                                <Badge variant="warning" size="sm">hidden</Badge>
                              )}
                              {col.auto_kill_days && (
                                <span className="text-xs text-gray-400">auto-kill: {col.auto_kill_days}d</span>
                              )}
                              {col.description && (
                                <span className="text-xs text-gray-400 italic">- {col.description}</span>
                              )}
                            </div>
                            {canManage && (
                              <div className="flex items-center gap-1 ml-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMoveColumn(index, 'up')}
                                  disabled={index === 0}
                                  title="Move up"
                                >
                                  <ChevronUp className="h-4 w-4 text-gray-400" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMoveColumn(index, 'down')}
                                  disabled={index === columns.length - 1}
                                  title="Move down"
                                >
                                  <ChevronDown className="h-4 w-4 text-gray-400" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingColumn(col.id);
                                    setEditColumnName(col.name);
                                    setEditColumnColor(col.color || '#6B7280');
                                    setEditColumnDescription(col.description || '');
                                  }}
                                  title="Edit column"
                                >
                                  <Pencil className="h-4 w-4 text-gray-400" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleColumnVisibility(col)}
                                  title={col.is_active ? 'Hide column' : 'Show column'}
                                >
                                  {col.is_active ? (
                                    <Eye className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <EyeOff className="h-4 w-4 text-gray-400" />
                                  )}
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => setDeleteColumnItem(col)}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Categories Tab */}
            <TabsContent value="categories">
              <div className="mt-6 space-y-6">
                {canManage && (
                  <form onSubmit={handleCreateCategory} className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label htmlFor="cat-name">New Category</Label>
                      <Input
                        id="cat-name"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="e.g. Bug Fix, Feature, Improvement"
                        className="mt-1"
                      />
                    </div>
                    <div className="w-44">
                      <Label htmlFor="cat-color">Color</Label>
                      <div className="mt-1">
                        <ColorPicker id="cat-color" value={newCategoryColor} onChange={setNewCategoryColor} />
                      </div>
                    </div>
                    <Button type="submit" disabled={creatingCategory || !newCategoryName.trim()}>
                      <Plus className="h-4 w-4 mr-1.5" />
                      Add
                    </Button>
                  </form>
                )}

                <div className="space-y-2">
                  {categoriesLoading ? (
                    <div className="p-8 text-center text-gray-500">Loading categories...</div>
                  ) : categories.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 dark:text-gray-500 text-sm">
                      No categories yet. Categories help group related items (e.g. Bug Fix, Feature, Improvement).
                    </div>
                  ) : (
                    categories.filter((cat): cat is Category => !!cat && !!cat.id).map((cat) => (
                      <div
                        key={cat.id}
                        className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800"
                      >
                        {editingCategory === cat.id ? (
                          <div className="flex items-center gap-3 flex-1 mr-2">
                            <ColorPicker size="sm" value={editCategoryColor} onChange={setEditCategoryColor} />
                            <Input
                              value={editCategoryName}
                              onChange={(e) => setEditCategoryName(e.target.value)}
                              className="flex-1"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); handleUpdateCategory(cat.id); }
                                if (e.key === 'Escape') setEditingCategory(null);
                              }}
                            />
                            <Button size="sm" onClick={() => handleUpdateCategory(cat.id)}>
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingCategory(null)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-3">
                              <div
                                className="h-4 w-4 rounded-full flex-shrink-0"
                                style={{ backgroundColor: cat.color || '#6B7280' }}
                              />
                              <span className="text-sm font-medium text-gray-900 dark:text-white">{cat.name}</span>
                            </div>
                            {canManage && (
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingCategory(cat.id);
                                    setEditCategoryName(cat.name);
                                    setEditCategoryColor(cat.color || '#6B7280');
                                  }}
                                >
                                  <Pencil className="h-4 w-4 text-gray-400" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => setDeleteCategoryItem(cat)}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Tags Tab */}
            <TabsContent value="tags">
              <div className="mt-6 space-y-6">
                {canManage && (
                  <form onSubmit={handleCreateTag} className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label htmlFor="tag-name">New Tag</Label>
                      <Input
                        id="tag-name"
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        placeholder="e.g. urgent, mobile, backend"
                        className="mt-1"
                      />
                    </div>
                    <div className="w-44">
                      <Label htmlFor="tag-color">Color</Label>
                      <div className="mt-1">
                        <ColorPicker id="tag-color" value={newTagColor} onChange={setNewTagColor} />
                      </div>
                    </div>
                    <Button type="submit" disabled={creatingTag || !newTagName.trim()}>
                      <Plus className="h-4 w-4 mr-1.5" />
                      Add
                    </Button>
                  </form>
                )}

                <div className="space-y-2">
                  {tagsLoading ? (
                    <div className="p-8 text-center text-gray-500">Loading tags...</div>
                  ) : tags.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 dark:text-gray-500 text-sm">
                      No tags yet. Tags let you label items with multiple keywords (e.g. urgent, mobile, backend).
                    </div>
                  ) : (
                    tags.map((tag) => (
                      <div
                        key={tag.id}
                        className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800"
                      >
                        {editingTag === tag.id ? (
                          <div className="flex items-center gap-3 flex-1 mr-2">
                            <ColorPicker size="sm" value={editTagColor} onChange={setEditTagColor} />
                            <Input
                              value={editTagName}
                              onChange={(e) => setEditTagName(e.target.value)}
                              className="flex-1"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); handleUpdateTag(tag.id); }
                                if (e.key === 'Escape') setEditingTag(null);
                              }}
                            />
                            <Button size="sm" onClick={() => handleUpdateTag(tag.id)}>
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingTag(null)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-3">
                              <Badge
                                size="sm"
                                style={{ backgroundColor: (tag.color || '#3B82F6') + '20', color: tag.color || '#3B82F6' }}
                              >
                                {tag.name}
                              </Badge>
                            </div>
                            {canManage && (
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingTag(tag.id);
                                    setEditTagName(tag.name);
                                    setEditTagColor(tag.color || '#3B82F6');
                                  }}
                                >
                                  <Pencil className="h-4 w-4 text-gray-400" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => setDeleteTagItem(tag)}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Branding Tab */}
            <TabsContent value="branding">
              <div className="mt-6 space-y-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Customize the appearance of your public roadmap and changelog pages.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="primary-color">Primary Color</Label>
                    <div className="mt-1">
                      <ColorPicker id="primary-color" value={primaryColor} onChange={setPrimaryColor} />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="secondary-color">Secondary Color</Label>
                    <div className="mt-1">
                      <ColorPicker id="secondary-color" value={secondaryColor} onChange={setSecondaryColor} />
                    </div>
                  </div>
                </div>
                <div>
                  <Label htmlFor="font-family">Font Family</Label>
                  <Select value={fontFamily} onValueChange={setFontFamily}>
                    <SelectItem value="Inter">Inter</SelectItem>
                    <SelectItem value="system-ui">System Default</SelectItem>
                    <SelectItem value="'DM Sans', sans-serif">DM Sans</SelectItem>
                    <SelectItem value="'Plus Jakarta Sans', sans-serif">Plus Jakarta Sans</SelectItem>
                    <SelectItem value="'Source Sans 3', sans-serif">Source Sans 3</SelectItem>
                    <SelectItem value="Georgia, serif">Georgia (Serif)</SelectItem>
                    <SelectItem value="'JetBrains Mono', monospace">JetBrains Mono</SelectItem>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="custom-css">Custom CSS</Label>
                  <textarea
                    id="custom-css"
                    value={customCss}
                    onChange={(e) => setCustomCss(e.target.value)}
                    placeholder="/* Override styles for your public pages */\n.roadmap-header { ... }"
                    rows={6}
                    className="mt-1 w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-mono text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Preview</span>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full" style={{ backgroundColor: primaryColor }} />
                    <div className="h-8 w-8 rounded-full" style={{ backgroundColor: secondaryColor }} />
                    <span style={{ fontFamily }} className="text-sm text-gray-900 dark:text-white">The quick brown fox jumps over the lazy dog</span>
                  </div>
                </div>
                {canManage && (
                  <div className="flex items-center gap-3">
                    <Button onClick={handleSaveTheme} disabled={savingTheme}>
                      {savingTheme ? 'Saving...' : 'Save Theme'}
                    </Button>
                    {themeSuccess && (
                      <span className="text-sm text-green-600 dark:text-green-400">Theme saved</span>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Remove member dialog */}
        <ConfirmDialog
          open={!!removeMember}
          onOpenChange={(open) => !open && setRemoveMember(null)}
          title="Remove Member"
          description={`Are you sure you want to remove ${removeMember?.name} from this workspace? They will lose access immediately.`}
          confirmLabel="Remove"
          variant="danger"
          onConfirm={confirmRemoveMember}
        />

        {/* Delete column dialog */}
        <ConfirmDialog
          open={!!deleteColumnItem}
          onOpenChange={(open) => !open && setDeleteColumnItem(null)}
          title="Delete Column"
          description={`Are you sure you want to delete the "${deleteColumnItem?.name}" column? Items in this column will need to be moved first.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={confirmDeleteColumn}
        />

        {/* Delete category dialog */}
        <ConfirmDialog
          open={!!deleteCategoryItem}
          onOpenChange={(open) => !open && setDeleteCategoryItem(null)}
          title="Delete Category"
          description={`Are you sure you want to delete the "${deleteCategoryItem?.name}" category? Items using this category will become uncategorized.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={confirmDeleteCategory}
        />

        {/* Delete tag dialog */}
        <ConfirmDialog
          open={!!deleteTagItem}
          onOpenChange={(open) => !open && setDeleteTagItem(null)}
          title="Delete Tag"
          description={`Are you sure you want to delete the "${deleteTagItem?.name}" tag? It will be removed from all items.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={confirmDeleteTag}
        />
      </div>
    </div>
  );
}
